import { Fragment, useEffect, useId, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { statusTone } from './format';
import { Icon, type IconName } from './icons';

export function Badge({ status, children, tone }: { status?: string; children?: ReactNode; tone?: 'good' | 'warn' | 'critical' | 'accent' | 'neutral' }) {
  const resolved = tone ?? statusTone(status ?? '');
  const cls =
    resolved === 'good'
      ? 'badge badge-good'
      : resolved === 'warn'
        ? 'badge badge-warn'
        : resolved === 'critical'
          ? 'badge badge-critical'
          : resolved === 'accent'
            ? 'badge badge-accent'
            : 'badge';
  return (
    <span className={cls}>
      {/* never colour alone */}
      {resolved !== 'neutral' && <span className="dot" />}
      {children ?? status}
    </span>
  );
}

export function Card({
  title,
  action,
  children,
  description,
  className,
  framed = false,
  style,
}: {
  title?: ReactNode;
  action?: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  className?: string;
  framed?: boolean;
  style?: CSSProperties;
}) {
  const cls = ['card', framed ? 'frame' : '', className ?? ''].filter(Boolean).join(' ');
  return (
    <div className={cls} style={style}>
      {(title || action) && (
        <div className="card-head">
          <div style={{ minWidth: 0 }}>
            {title && <h2>{title}</h2>}
            {description && <p className="subtitle">{description}</p>}
          </div>
          {action}
        </div>
      )}
      {children}
    </div>
  );
}

export function PageHeader({
  title,
  subtitle,
  actions,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="page-head">
      <div style={{ minWidth: 0 }}>
        <h1>{title}</h1>
        {subtitle && <p className="subtitle">{subtitle}</p>}
      </div>
      {actions && <div className="row wrap">{actions}</div>}
    </div>
  );
}

const ALERT_ICON: Record<'info' | 'error' | 'success' | 'warn', IconName> = {
  info: 'info',
  error: 'error',
  success: 'success',
  warn: 'warn',
};

export function Alert({ kind = 'info', children }: { kind?: 'info' | 'error' | 'success' | 'warn'; children: ReactNode }) {
  return (
    <div className={`alert alert-${kind}`} role={kind === 'error' ? 'alert' : undefined}>
      <Icon name={ALERT_ICON[kind]} size={16} className="alert-icon" />
      <div style={{ minWidth: 0 }}>{children}</div>
    </div>
  );
}

export function Empty({ children, action, icon = 'boxes' }: { children: ReactNode; action?: ReactNode; icon?: IconName }) {
  return (
    <div className="empty">
      <div className="empty-icon">
        <Icon name={icon} size={20} />
      </div>
      <div>{children}</div>
      {action}
    </div>
  );
}

export function Loading({ label = 'Memuat' }: { label?: string }) {
  return (
    <div className="row" style={{ padding: '36px 0', justifyContent: 'center', color: 'var(--text-muted)', fontSize: 13.5 }}>
      <span className="spinner" />
      <span>{label}…</span>
    </div>
  );
}

export function Modal({
  title,
  onClose,
  children,
  width = 480,
  dismissible = true,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  width?: number;
  dismissible?: boolean;
}) {
  useEffect(() => {
    if (!dismissible) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, dismissible]);

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" aria-label={title} onClick={dismissible ? onClose : undefined}>
      <div className="modal frame" onClick={(event) => event.stopPropagation()} style={{ maxWidth: width }}>
        <div className="modal-head">
          <h2>{title}</h2>
          {dismissible && (
            <button className="btn btn-sm btn-ghost icon-btn" onClick={onClose} aria-label="Tutup">
              <Icon name="close" size={16} />
            </button>
          )}
        </div>
        <div className="modal-body">{children}</div>
      </div>
    </div>
  );
}

type Theme = 'light' | 'dark' | 'system';

function readTheme(): Theme {
  try {
    return (localStorage.getItem('aiagg.theme') as Theme | null) ?? 'system';
  } catch {
    return 'system';
  }
}

/** Before render: no flash. */
export function applyStoredTheme() {
  const theme = readTheme();
  if (theme === 'system') document.documentElement.removeAttribute('data-theme');
  else document.documentElement.setAttribute('data-theme', theme);
}

export type ThemePreference = Theme;
const THEME_EVENT = 'aiagg:theme';

/** Sets theme; syncs toggles. */
export function setStoredTheme(theme: Theme) {
  if (theme === 'system') document.documentElement.removeAttribute('data-theme');
  else document.documentElement.setAttribute('data-theme', theme);
  try {
    localStorage.setItem('aiagg.theme', theme);
  } catch {
    // storage blocked
  }
  window.dispatchEvent(new CustomEvent<Theme>(THEME_EVENT, { detail: theme }));
}

export function useThemePreference(): Theme {
  const [theme, setTheme] = useState<Theme>(readTheme);
  useEffect(() => {
    const onTheme = (event: Event) => setTheme((event as CustomEvent<Theme>).detail);
    window.addEventListener(THEME_EVENT, onTheme);
    return () => window.removeEventListener(THEME_EVENT, onTheme);
  }, []);
  return theme;
}

export function ThemeToggle({ withLabel = false }: { withLabel?: boolean }) {
  const [theme, setTheme] = useState<Theme>(readTheme);

  useEffect(() => {
    if (theme === 'system') document.documentElement.removeAttribute('data-theme');
    else document.documentElement.setAttribute('data-theme', theme);
    try {
      localStorage.setItem('aiagg.theme', theme);
    } catch {
      // storage blocked
    }
  }, [theme]);

  // sync other toggles
  useEffect(() => {
    const onTheme = (event: Event) => setTheme((event as CustomEvent<Theme>).detail);
    window.addEventListener(THEME_EVENT, onTheme);
    return () => window.removeEventListener(THEME_EVENT, onTheme);
  }, []);

  const next = () => setStoredTheme(theme === 'system' ? 'dark' : theme === 'dark' ? 'light' : 'system');
  const icon: IconName = theme === 'light' ? 'sun' : theme === 'dark' ? 'moon' : 'monitor';
  const label = theme === 'light' ? 'Terang' : theme === 'dark' ? 'Gelap' : 'Sistem';

  return (
    <button
      className={withLabel ? 'btn btn-sm' : 'btn btn-sm icon-btn'}
      onClick={next}
      title={`Tema: ${label}`}
      aria-label={`Tema: ${label}`}
    >
      <Icon name={icon} size={16} />
      {withLabel && label}
    </button>
  );
}

export function CopyButton({ value, label = 'Salin', className }: { value: string; label?: string; className?: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      className={className ? `btn btn-sm ${className}` : 'btn btn-sm'}
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(value);
          setCopied(true);
          setTimeout(() => setCopied(false), 1600);
        } catch {
          setCopied(false);
        }
      }}
    >
      <Icon name={copied ? 'check' : 'copy'} size={14} />
      {copied ? 'Tersalin' : label}
    </button>
  );
}

const KEYWORDS = new Set([
  'import', 'from', 'const', 'let', 'var', 'await', 'async', 'def', 'print', 'for', 'while', 'in', 'of', 'if', 'else',
  'return', 'new', 'true', 'false', 'True', 'False', 'None', 'null', 'function', 'require', 'use', 'echo', 'curl',
  'export', 'with', 'as', 'try', 'except', 'catch', 'break', 'continue', 'class', 'php',
]);

const TOKEN = /(#[^\n]*|\/\/[^\n]*)|("(?:[^"\\\n]|\\.)*"|'(?:[^'\\\n]|\\.)*'|`(?:[^`\\]|\\.)*`)|(\b\d[\d_.]*\b)|(\$[A-Za-z_]\w*|\b[A-Za-z_]\w*\b)|(\s--?[a-zA-Z][\w-]*)/g;

function highlight(code: string): ReactNode[] {
  const out: ReactNode[] = [];
  let last = 0;
  let key = 0;
  for (const match of code.matchAll(TOKEN)) {
    const index = match.index ?? 0;
    if (index > last) out.push(code.slice(last, index));
    const [text, comment, str, number, word, flag] = match;
    // URL, not comment
    if (comment && text.startsWith('//') && /:\s*$/.test(code.slice(Math.max(0, index - 6), index))) {
      out.push(text);
    } else if (comment) out.push(<span key={key++} className="tok-c">{text}</span>);
    else if (str) out.push(<span key={key++} className="tok-s">{text}</span>);
    else if (number) out.push(<span key={key++} className="tok-n">{text}</span>);
    else if (flag) out.push(<span key={key++} className="tok-p">{text}</span>);
    else if (word && (KEYWORDS.has(word) || word.startsWith('$'))) out.push(<span key={key++} className="tok-k">{text}</span>);
    else out.push(text);
    last = index + text.length;
  }
  if (last < code.length) out.push(code.slice(last));
  return out;
}

export function CodeBlock({ code, label, tabs }: { code: string; label?: string; tabs?: ReactNode }) {
  return (
    <div className="code-block">
      {tabs ? (
        <div className="code-head" style={{ paddingLeft: 4 }}>
          {tabs}
          <CopyButton value={code} />
        </div>
      ) : (
        <div className="code-head">
          <span>{label ?? 'kode'}</span>
          <CopyButton value={code} />
        </div>
      )}
      <pre>
        {highlight(code).map((part, i) => (
          <Fragment key={i}>{part}</Fragment>
        ))}
      </pre>
    </div>
  );
}

export function Tabs<T extends string>({
  tabs,
  value,
  onChange,
  tight = false,
}: {
  tabs: ReadonlyArray<{ id: T; label: ReactNode; count?: number; icon?: IconName }>;
  value: T;
  onChange: (id: T) => void;
  tight?: boolean;
}) {
  return (
    <div className={tight ? 'tabs tight' : 'tabs'} role="tablist">
      {tabs.map((tab) => (
        <button
          key={tab.id}
          role="tab"
          aria-selected={tab.id === value}
          className={tab.id === value ? 'tab active' : 'tab'}
          onClick={() => onChange(tab.id)}
        >
          {tab.icon && <Icon name={tab.icon} size={15} />}
          {tab.label}
          {tab.count !== undefined && <span className="count">{tab.count}</span>}
        </button>
      ))}
    </div>
  );
}

export function Segmented<T extends string>({
  options,
  value,
  onChange,
  label,
}: {
  options: ReadonlyArray<{ id: T; label: ReactNode }>;
  value: T;
  onChange: (id: T) => void;
  label?: string;
}) {
  return (
    <div className="seg" role="radiogroup" aria-label={label}>
      {options.map((option) => (
        <button
          key={option.id}
          role="radio"
          aria-checked={option.id === value}
          className={option.id === value ? 'on' : ''}
          onClick={() => onChange(option.id)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

/** Change vs previous period. */
export function Delta({ current, previous, invert = false }: { current: number; previous: number; invert?: boolean }) {
  if (!previous) return <span className="delta flat">Baru</span>;
  const change = ((current - previous) / previous) * 100;
  if (Math.abs(change) < 0.5) return <span className="delta flat">± 0%</span>;
  const good = invert ? change < 0 : change > 0;
  return (
    <span className={`delta ${good ? 'up' : 'down'}`}>
      {change > 0 ? '▲' : '▼'} {Math.abs(change).toLocaleString('id-ID', { maximumFractionDigits: Math.abs(change) > 99 ? 0 : 1 })}%
    </span>
  );
}

export function KeyValue({ items }: { items: Array<[ReactNode, ReactNode]> }) {
  return (
    <dl style={{ display: 'grid', gridTemplateColumns: 'auto minmax(0, 1fr)', gap: '8px 18px', margin: 0, fontSize: 13 }}>
      {items.map(([k, v], i) => (
        <Fragment key={i}>
          <dt className="label" style={{ paddingTop: 2 }}>{k}</dt>
          <dd style={{ margin: 0, color: 'var(--text-primary)', minWidth: 0, overflowWrap: 'anywhere' }}>{v}</dd>
        </Fragment>
      ))}
    </dl>
  );
}

export function InfoTip({
  children,
  label,
  trigger,
  width = 280,
  className,
}: {
  children: ReactNode;
  /** Accessible name. */
  label?: string;
  trigger?: ReactNode;
  width?: number;
  className?: string;
}) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number; above: boolean; arrow: number } | null>(null);
  const btn = useRef<HTMLButtonElement>(null);
  const tip = useRef<HTMLDivElement>(null);
  const timer = useRef<number | undefined>(undefined);

  const show = () => {
    window.clearTimeout(timer.current);
    setOpen(true);
  };
  const hide = () => {
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setOpen(false), 90);
  };

  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    const place = () => {
      const el = btn.current;
      const box = tip.current;
      if (!el || !box) return;
      const r = el.getBoundingClientRect();
      const w = box.offsetWidth;
      const h = box.offsetHeight;
      const gap = 8;
      // flip when cramped
      const above = r.top - h - gap >= 8 || window.innerHeight - r.bottom < h + gap + 8;
      const center = r.left + r.width / 2;
      const left = Math.min(Math.max(8, center - w / 2), Math.max(8, window.innerWidth - w - 8));
      setPos({ top: above ? r.top - h - gap : r.bottom + gap, left, above, arrow: Math.min(Math.max(12, center - left), w - 12) });
    };
    place();
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      // don't close modal too
      event.stopPropagation();
      setOpen(false);
    };
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    window.addEventListener('keydown', onKey, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('keydown', onKey, true);
    };
  }, [open]);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  return (
    <>
      <button
        ref={btn}
        type="button"
        className={['infotip-btn', trigger ? 'custom' : '', className ?? ''].filter(Boolean).join(' ')}
        aria-label={label ?? (trigger ? undefined : 'Info')}
        aria-describedby={id}
        aria-expanded={open}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        onClick={(event) => {
          event.preventDefault();
          show();
        }}
      >
        {trigger ?? <Icon name="info" size={14} />}
      </button>
      {createPortal(
        <div
          ref={tip}
          id={id}
          role="tooltip"
          className={[open && pos ? 'infotip open' : 'infotip', pos?.above === false ? 'below' : ''].filter(Boolean).join(' ')}
          style={{ top: pos?.top ?? 0, left: pos?.left ?? 0, maxWidth: `min(${width}px, calc(100vw - 16px))`, ['--arrow' as string]: `${pos?.arrow ?? 0}px` }}
          onMouseEnter={open ? show : undefined}
          onMouseLeave={open ? hide : undefined}
        >
          {children}
        </div>,
        document.body,
      )}
    </>
  );
}

export { Icon } from './icons';
export type { IconName } from './icons';
export { Select } from './select';
export type { SelectOption } from './select';
